import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { BookOpen, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import { Exercise, LearningMode, ProgrammingLanguage } from '@unity-learning/shared';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { createMiniCours, getExerciseMiniCours } from '../services/api';
import MiniCoursContent from './MiniCoursContent';
import { trackEvent } from '../utils/analytics';
import { buildMiniCoursBasePrompt, buildMiniCoursSimplifyPrompt } from '../utils/miniCoursPrompts';

/**
 * Panneau « Mini-cours : Aide contextuelle » de la page d'exercice.
 *
 * Ce composant vivait DANS le corps de `ExercisePage` (`const MiniCours =
 * () => …`). Une fonction-composant recréée à chaque rendu du parent est, pour React, un
 * nouveau type → démontage/remontage à chaque frappe dans l'éditeur (`setCode`) : le panneau
 * se refermait dès que l'étudiant tapait un caractère, et `GET /api/exercises/:id/mini-cours`
 * repartait à chaque rendu (6 appels au chargement, +1 par frappe). Hissé au niveau module,
 * il garde son état (ouvert, niveau de simplification, explications) tant que l'exercice
 * ne change pas.
 */
const ExerciseMiniCours: React.FC<{ exercise: Exercise }> = ({ exercise }) => {
  const { user } = useUser();
  const { learningMode, programmingLanguage } = useLearningMode();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [explanations, setExplanations] = useState<string[]>([]);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [hasLoadedFromDatabase, setHasLoadedFromDatabase] = useState(false);
  const maxLevels = 10;
  const userId = user?.id;

  // Load existing mini-courses from database (once per exercise/user)
  useEffect(() => {
    if (!userId || !exercise) return;
    let cancelled = false;

    (async () => {
      try {
        const records = await getExerciseMiniCours(exercise.id, userId);
        if (cancelled) return;
        if (records && records.length > 0) {
          const sortedMiniCours = [...records].sort((a, b) => a.simplification_level - b.simplification_level);
          setExplanations(sortedMiniCours.map((mc) => mc.explanation));
          setCurrentLevel(sortedMiniCours[sortedMiniCours.length - 1].simplification_level);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des mini-cours:', error);
      } finally {
        if (!cancelled) setHasLoadedFromDatabase(true);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, exercise?.id]);

  const generateMiniCours = async (simplificationLevel: number = 0) => {
    if (!user || !exercise) return;

    setIsLoading(true);
    try {
      const modeLabel = learningMode === LearningMode.GODOT
        ? 'Godot/GDScript'
        : learningMode === LearningMode.PYTHON_JS
          ? (programmingLanguage === ProgrammingLanguage.PYTHON ? 'Python' : 'JavaScript')
          : 'Unity/C#';

      const prompt = simplificationLevel === 0
        ? buildMiniCoursBasePrompt({
            title: exercise.title,
            description: exercise.description,
            concepts: exercise.concepts,
            modeLabel,
          })
        : buildMiniCoursSimplifyPrompt(explanations[explanations.length - 1], simplificationLevel);

      const result = await createMiniCours({
        userId: user.id,
        exerciseId: exercise.id,
        simplificationLevel,
        prompt
      });
      if (!result.fromCache) {
        // Only add to explanations if it's newly generated
        setExplanations(prev => [...prev, result.explanation]);
      }
      setCurrentLevel(simplificationLevel);
    } catch (error) {
      console.error('Erreur mini-cours:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSimplify = () => {
    if (currentLevel < maxLevels - 1) {
      generateMiniCours(currentLevel + 1);
    }
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden">
      <button
        onClick={() => {
          if (!isOpen) trackEvent('mini_course_opened', { exerciseId: exercise.id });
          setIsOpen(!isOpen);
          if (!isOpen && explanations.length === 0 && hasLoadedFromDatabase) {
            generateMiniCours(0);
          }
        }}
        className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <span className="font-medium text-blue-900 dark:text-blue-300">Mini-cours : Aide contextuelle</span>
          <span className="text-sm text-blue-600 bg-blue-200 px-2 py-1 rounded-full">
            Optionnel
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        )}
      </button>

      {isOpen && (
        <div className="p-4 bg-white dark:bg-gray-800 border-t border-blue-200 dark:border-blue-800">
          {isLoading && explanations.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-3 text-blue-600 dark:text-blue-400">
                <div className="w-6 h-6 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Génération du mini-cours...</span>
              </div>
            </div>
          ) : explanations.length > 0 ? (
            <div className="space-y-4">
              <MiniCoursContent markdown={explanations[explanations.length - 1]} />

              <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <Lightbulb className="w-4 h-4" />
                  <span>
                    Niveau de simplification: {currentLevel === 0 ? 'Standard' : `${currentLevel}/10`}
                  </span>
                </div>

                {currentLevel < maxLevels - 1 && (
                  <button
                    onClick={handleSimplify}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-2 bg-yellow-500 text-white text-sm font-medium rounded-md hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Simplification...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-1" />
                        Plus simple
                      </>
                    )}
                  </button>
                )}

                {currentLevel >= maxLevels - 1 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Niveau de simplification maximum atteint
                  </span>
                )}
              </div>

              {explanations.length > 1 && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
                    Voir les explications précédentes ({explanations.length - 1})
                  </summary>
                  <div className="mt-2 space-y-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                    {explanations.slice(0, -1).map((explanation, index) => (
                      <div key={index} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                          Niveau {index === 0 ? 'Standard' : index}
                        </div>
                        <ReactMarkdown
                          components={{
                            strong: ({ children }) => <strong className="font-bold text-gray-800 dark:text-gray-200">{children}</strong>,
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-2 border-gray-400 dark:border-gray-500 pl-2 italic text-gray-600 dark:text-gray-300">
                                {children}
                              </blockquote>
                            ),
                            p: ({ children }) => <p className="text-sm text-gray-600 dark:text-gray-300 mb-2 last:mb-0">{children}</p>,
                          }}
                        >
                          {explanation}
                        </ReactMarkdown>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
              Cliquez pour ouvrir et générer le mini-cours
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExerciseMiniCours;
